import { useCallback, useMemo } from 'react'

interface StatusCheckState {
  viewedAt: number
}

type GrantsAction = 'clicked' | 'dismissed'
interface GrantsState {
  action: GrantsAction
  timestamp: number
  actionCount: number
}

type SmartApplyAction = 'applied' | 'dismissed'
interface SmartApplyState {
  action: SmartApplyAction
  timestamp: number
  actionCount: number
}

type SecondSmartApplyAction = 'applied' | 'dismissed'
interface SecondSmartApplyState {
  action: SecondSmartApplyAction
  timestamp: number
}

type InterestModalAction = 'viewed' | 'registered'

// The answer is experiment-wide: registering on any touchpoint quiets the modal. `declined`
// comes only from the modal's "I'm not interested"; Escape is not an answer.
type GfmInterestModalAction = 'viewed' | 'registered' | 'declined'
interface GfmInterestModalState {
  action: GfmInterestModalAction
  timestamp: number
  viewCount: number
}
interface InterestModalState {
  action: InterestModalAction
  timestamp: number
  viewCount: number
}

const STATUS_CHECK_KEY = 'preference_statusCheck'
const GRANTS_KEY = 'preference_grants'
const SMART_APPLY_KEY = 'preference_smartApply'
const SECOND_SMART_APPLY_KEY = 'preference_secondSmartApply'
const INTEREST_MODAL_KEY = 'preference_interestModal'
// Same 3-views-12-hours-apart pattern as Smart Apply, under its own key so anyone who saw the
// earlier smoke test starts fresh.
const GFM_INTEREST_MODAL_KEY = 'preference_gfmInterestModal'

const MAX_VIEWS = 3
const HOURS_BETWEEN_VIEWS = 12
const HOURS_BEFORE_SECOND_SMART_APPLY = 24

export const useIntroModalState = () => {
  const getStatusCheckState = useCallback((): StatusCheckState | null => {
    const stored = localStorage.getItem(STATUS_CHECK_KEY)
    if (!stored) {
      return null
    }
    try {
      return JSON.parse(stored) as StatusCheckState
    } catch {
      return null
    }
  }, [])

  const shouldShowStatusCheck = useMemo(() => {
    const state = getStatusCheckState()
    return !state
  }, [getStatusCheckState])

  const recordStatusCheckAction = useCallback((viewed: Date) => {
    const newState: StatusCheckState = {
      viewedAt: viewed.getTime()
    }
    localStorage.setItem(STATUS_CHECK_KEY, JSON.stringify(newState))
  }, [])

  const getGrantsState = useCallback((): GrantsState | null => {
    const stored = localStorage.getItem(GRANTS_KEY)
    if (!stored) {
      return null
    }
    try {
      return JSON.parse(stored) as GrantsState
    } catch {
      return null
    }
  }, [])

  const shouldShowGrants = useMemo(() => {
    const state = getGrantsState()

    if (!state) {
      return true
    }

    if (state.action === 'clicked') {
      return false
    }

    if (state.actionCount >= MAX_VIEWS) {
      return false
    }

    const now = Date.now()
    const hoursSinceLastAction = (now - state.timestamp) / (1000 * 60 * 60)
    return hoursSinceLastAction >= HOURS_BETWEEN_VIEWS
  }, [getGrantsState])

  const recordGrantsAction = useCallback(
    (action: GrantsAction) => {
      const currentState = getGrantsState()
      const newState: GrantsState = {
        action,
        timestamp: Date.now(),
        actionCount: currentState ? currentState.actionCount + 1 : 1
      }
      localStorage.setItem(GRANTS_KEY, JSON.stringify(newState))
    },
    [getGrantsState]
  )

  const getSmartApplyState = useCallback((): SmartApplyState | null => {
    const stored = localStorage.getItem(SMART_APPLY_KEY)
    if (!stored) {
      return null
    }
    try {
      return JSON.parse(stored) as SmartApplyState
    } catch {
      return null
    }
  }, [])

  const shouldShowSmartApply = useMemo(() => {
    const state = getSmartApplyState()

    if (!state) {
      return true
    }

    if (state.action === 'applied') {
      return false
    }

    if (state.actionCount >= MAX_VIEWS) {
      return false
    }

    const now = Date.now()
    const hoursSinceLastAction = (now - state.timestamp) / (1000 * 60 * 60)
    return hoursSinceLastAction >= HOURS_BETWEEN_VIEWS
  }, [getSmartApplyState])

  const recordSmartApplyAction = useCallback(
    (action: SmartApplyAction) => {
      const currentState = getSmartApplyState()
      const newState: SmartApplyState = {
        action,
        timestamp: Date.now(),
        actionCount: currentState ? currentState.actionCount + 1 : 1
      }
      localStorage.setItem(SMART_APPLY_KEY, JSON.stringify(newState))
    },
    [getSmartApplyState]
  )

  const getSecondSmartApplyState = useCallback((): SecondSmartApplyState | null => {
    const stored = localStorage.getItem(SECOND_SMART_APPLY_KEY)
    if (!stored) {
      return null
    }
    try {
      return JSON.parse(stored) as SecondSmartApplyState
    } catch {
      return null
    }
  }, [])

  const shouldShowSecondSmartApply = useMemo(() => {
    const smartApplyState = getSmartApplyState()

    if (smartApplyState?.action !== 'dismissed' || smartApplyState.actionCount < MAX_VIEWS) {
      return false
    }

    const hoursSinceLastDismissal = (Date.now() - smartApplyState.timestamp) / (1000 * 60 * 60)
    if (hoursSinceLastDismissal < HOURS_BEFORE_SECOND_SMART_APPLY) {
      return false
    }

    const secondState = getSecondSmartApplyState()
    return !secondState
  }, [getSmartApplyState, getSecondSmartApplyState])

  const recordSecondSmartApplyAction = useCallback((action: SecondSmartApplyAction) => {
    const newState: SecondSmartApplyState = {
      action,
      timestamp: Date.now()
    }
    localStorage.setItem(SECOND_SMART_APPLY_KEY, JSON.stringify(newState))
  }, [])

  const getInterestModalState = useCallback((): InterestModalState | null => {
    const stored = localStorage.getItem(INTEREST_MODAL_KEY)
    if (!stored) {
      return null
    }
    try {
      return JSON.parse(stored) as InterestModalState
    } catch {
      return null
    }
  }, [])

  const shouldShowInterestModal = useMemo(() => {
    const state = getInterestModalState()

    if (!state) {
      return true
    }

    if (state.action === 'registered') {
      return false
    }

    if (state.viewCount >= MAX_VIEWS) {
      return false
    }

    const now = Date.now()
    const hoursSinceLastView = (now - state.timestamp) / (1000 * 60 * 60)
    return hoursSinceLastView >= HOURS_BETWEEN_VIEWS
  }, [getInterestModalState])

  const getGfmInterestModalState = useCallback((): GfmInterestModalState | null => {
    const stored = localStorage.getItem(GFM_INTEREST_MODAL_KEY)
    if (!stored) {
      return null
    }
    try {
      return JSON.parse(stored) as GfmInterestModalState
    } catch {
      return null
    }
  }, [])

  const shouldShowGfmInterestModal = useMemo(() => {
    const state = getGfmInterestModalState()

    if (!state) {
      return true
    }

    if (state.action === 'registered' || state.action === 'declined') {
      return false
    }

    if (state.viewCount >= MAX_VIEWS) {
      return false
    }

    const hoursSinceLastView = (Date.now() - state.timestamp) / (1000 * 60 * 60)
    return hoursSinceLastView >= HOURS_BETWEEN_VIEWS
  }, [getGfmInterestModalState])

  const recordGfmInterestModalView = useCallback(() => {
    const current = getGfmInterestModalState()
    localStorage.setItem(
      GFM_INTEREST_MODAL_KEY,
      JSON.stringify({
        action: 'viewed',
        timestamp: Date.now(),
        viewCount: current ? current.viewCount + 1 : 1
      } satisfies GfmInterestModalState)
    )
  }, [getGfmInterestModalState])

  const recordGfmInterestModalAnswer = useCallback(
    (action: Extract<GfmInterestModalAction, 'registered' | 'declined'>) => {
      const current = getGfmInterestModalState()
      localStorage.setItem(
        GFM_INTEREST_MODAL_KEY,
        JSON.stringify({
          action,
          timestamp: Date.now(),
          viewCount: current ? current.viewCount : 1
        } satisfies GfmInterestModalState)
      )
    },
    [getGfmInterestModalState]
  )

  const recordGfmInterestRegistered = useCallback(
    () => recordGfmInterestModalAnswer('registered'),
    [recordGfmInterestModalAnswer]
  )

  const recordGfmInterestDeclined = useCallback(
    () => recordGfmInterestModalAnswer('declined'),
    [recordGfmInterestModalAnswer]
  )

  const getInterestModalViewNumber = useCallback((): number => {
    const state = getInterestModalState()
    return Math.min((state?.viewCount ?? 0) + 1, MAX_VIEWS)
  }, [getInterestModalState])

  const recordInterestModalView = useCallback(() => {
    const currentState = getInterestModalState()
    const newState: InterestModalState = {
      action: 'viewed',
      timestamp: Date.now(),
      viewCount: currentState ? currentState.viewCount + 1 : 1
    }
    localStorage.setItem(INTEREST_MODAL_KEY, JSON.stringify(newState))
  }, [getInterestModalState])

  const recordInterestModalRegistered = useCallback(() => {
    const currentState = getInterestModalState()
    const newState: InterestModalState = {
      action: 'registered',
      timestamp: Date.now(),
      viewCount: currentState ? currentState.viewCount : 1
    }
    localStorage.setItem(INTEREST_MODAL_KEY, JSON.stringify(newState))
  }, [getInterestModalState])

  return {
    shouldShowStatusCheck,
    recordStatusCheckAction,
    shouldShowGrants,
    recordGrantsAction,
    shouldShowSmartApply,
    recordSmartApplyAction,
    shouldShowSecondSmartApply,
    recordSecondSmartApplyAction,
    shouldShowInterestModal,
    shouldShowGfmInterestModal,
    recordGfmInterestModalView,
    recordGfmInterestRegistered,
    recordGfmInterestDeclined,
    getInterestModalViewNumber,
    recordInterestModalView,
    recordInterestModalRegistered
  }
}
